import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  Divider,
  FormLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import SimpleBar from 'simplebar-react';
import { z } from 'zod';

import { DEFAULT_PAGINATION } from '@/constants/api';
import { useUpdateLocationMutation } from '@/store/api/services/locations/location';
import { useGetMerchantsQuery } from '@/store/api/services/merchants/merchants';
import type { Location } from '@/types/location';
import type { Merchant } from '@/types/merchant';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';

const BulkAssignMerchantSchema = z.object({
  merchant: z.custom<Merchant>().nullable(),
});

type BulkAssignMerchantSchemaType = z.infer<typeof BulkAssignMerchantSchema>;

const BulkAssignMerchant = ({
  hide,
  props,
}: ModalChildrenProps & { props?: { locationIds?: number[]; locations?: Location[] } }) => {
  const [updateLocation, { isLoading: isUpdating }] = useUpdateLocationMutation();
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [results, setResults] = useState<
    Array<{ location_id: number; success: boolean; error?: string }>
  >([]);
  const [locations, setLocations] = useState<Location[]>(props?.locations || []);

  // Initialize locations from props
  useEffect(() => {
    if (props?.locations) {
      setLocations(props.locations);
    }
  }, [props?.locations]);

  // Derive locationIds from locations
  const locationIds = locations.map((location) => location.location_id);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<BulkAssignMerchantSchemaType>({
    resolver: zodResolver(BulkAssignMerchantSchema),
    mode: 'onSubmit',
    defaultValues: {
      merchant: null,
    },
  });

  // Fetch merchants
  const { data: merchantsData, isLoading: isMerchantsLoading } = useGetMerchantsQuery({
    pagination: {
      current_page: DEFAULT_PAGINATION.current_page,
      items_per_page: 100, // Get more merchants for selection
      sort_ascending: true,
      sort_by: 'company_name',
    },
  });

  const merchants = merchantsData?.data || [];

  // Handle removing a location from the list
  const handleRemoveLocation = (locationId: number) => {
    setLocations((prev) => prev.filter((location) => location.location_id !== locationId));
  };

  const onSubmit = async (data: BulkAssignMerchantSchemaType) => {
    if (!data.merchant || !locationIds.length) {
      toast.error('Please select a merchant and ensure locations are selected.');
      return;
    }

    try {
      const updatePromises = locationIds.map(async (locationId) => {
        try {
          await updateLocation({
            location_id: locationId,
            merchant_id: data.merchant!.id,
            hours: ['24/7'], // Required field
          } as any).unwrap();
          return { location_id: locationId, success: true };
        } catch (error: any) {
          return {
            location_id: locationId,
            success: false,
            error: error?.data?.message || error?.message || 'Unknown error',
          };
        }
      });

      const results = await Promise.all(updatePromises);
      setResults(results);

      const successCount = results.filter((r) => r.success).length;
      const failureCount = results.length - successCount;

      if (successCount === results.length) {
        toast.success(`Successfully assigned merchant to ${successCount} location(s).`);
        setTimeout(() => {
          hide();
        }, 1500);
      } else {
        toast.error(
          `Assigned merchant to ${successCount} location(s), but ${failureCount} failed.`,
        );
      }
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Failed to assign merchant');
    }
  };

  // Filter options for merchant autocomplete
  const filterOptions = (options: Merchant[], { inputValue }: { inputValue: string }) => {
    if (!inputValue) return options;
    const searchValue = inputValue.toLowerCase();
    return options.filter(
      (merchant) =>
        merchant?.company_name?.toLowerCase().includes(searchValue) ||
        merchant?.owner_first_name?.toLowerCase().includes(searchValue) ||
        merchant?.owner_last_name?.toLowerCase().includes(searchValue) ||
        merchant?.owner_email?.toLowerCase().includes(searchValue) ||
        merchant?.id?.toLowerCase().includes(searchValue.replace(/-/g, '')),
    );
  };

  return (
    <>
      <DialogContent sx={{ py: 3 }}>
        <Stack gap={3}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Assign Merchant to {locationIds.length} Location{locationIds.length !== 1 ? 's' : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a merchant to assign to the selected locations.
            </Typography>

            {/* Show selected locations */}
            <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Selected Locations ({locationIds.length})
              </Typography>
              {locations.length > 0 ? (
                <SimpleBar style={{ maxHeight: 200 }} autoHide={false}>
                  <List dense sx={{ py: 0 }}>
                    {locations.map((location) => (
                      <ListItem
                        key={location.location_id}
                        sx={{ px: 0, py: 0.5 }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRemoveLocation(location.location_id)}
                            sx={{ color: 'text.secondary' }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {location.location_name || `Location ${location.location_id}`}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              ID: {location.location_id} • {location.address || 'No address'}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </SimpleBar>
              ) : (
                <Stack spacing={0.5}>
                  {locationIds.map((locationId) => (
                    <Typography key={locationId} variant="body2" color="text.secondary">
                      Location ID: {locationId}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>

          <Controller
            name="merchant"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                options={merchants}
                loading={isMerchantsLoading}
                value={value as Merchant | null}
                onChange={(_, newValue) => {
                  onChange(newValue);
                  setSelectedMerchant(newValue);
                }}
                filterOptions={filterOptions}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  return (
                    option.company_name || `${option.owner_first_name} ${option.owner_last_name}`
                  );
                }}
                isOptionEqualToValue={(option, val) => {
                  if (!option || !val) return false;
                  return option.id === val.id;
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {option.company_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.owner_first_name} {option.owner_last_name} • {option.owner_email}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={option.is_serviceable ? 'Serviceable' : 'Not Serviceable'}
                        color={option.is_serviceable ? 'success' : 'default'}
                      />
                    </Stack>
                  </Box>
                )}
                renderInput={(params) => (
                  <>
                    <FormLabel sx={{ fontSize: '12px' }}>Select Merchant</FormLabel>
                    <TextField
                      {...params}
                      placeholder="Search by company name, owner name, or email"
                      error={!!errors.merchant}
                      helperText={errors.merchant?.message}
                      size="small"
                    />
                  </>
                )}
              />
            )}
          />

          {selectedMerchant && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Selected Merchant Details
              </Typography>
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  <strong>Company:</strong> {selectedMerchant.company_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Owner:</strong> {selectedMerchant.owner_first_name}{' '}
                  {selectedMerchant.owner_last_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {selectedMerchant.owner_email}
                </Typography>
                <Typography variant="body2">
                  <strong>Phone:</strong> {selectedMerchant.owner_phone_number}
                </Typography>
                <Typography variant="body2">
                  <strong>Classification:</strong>{' '}
                  <Chip
                    size="small"
                    label={selectedMerchant.classification}
                    sx={{ ml: 0.5, textTransform: 'capitalize' }}
                  />
                </Typography>
                <Typography variant="body2">
                  <strong>Serviceable:</strong>{' '}
                  <Chip
                    size="small"
                    label={selectedMerchant.is_serviceable ? 'Yes' : 'No'}
                    color={selectedMerchant.is_serviceable ? 'success' : 'default'}
                    sx={{ ml: 0.5 }}
                  />
                </Typography>
              </Stack>
            </Box>
          )}

          {results.length > 0 && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Assignment Results
              </Typography>
              <Stack spacing={0.5}>
                {results.map((result) => (
                  <Typography
                    key={result.location_id}
                    variant="body2"
                    color={result.success ? 'success.main' : 'error.main'}
                  >
                    Location {result.location_id}:{' '}
                    {result.success ? 'Success' : `Failed - ${result.error}`}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
        <Button variant="text" onClick={hide} disabled={isSubmitting || isUpdating}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={isSubmitting || isUpdating}
          onClick={handleSubmit(onSubmit)}
          disabled={!selectedMerchant || locationIds.length === 0}
        >
          Assign Merchant
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default BulkAssignMerchant;
